import {FC, ReactNode, useCallback, useMemo, useRef, useState} from 'react';
import {useFormContext} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import clsx from 'clsx';
import Button from 'components/Button';
import DropdownSelectItem from 'components/Dropdown/DropdownSelect/DropdownSelectItem';
import Overlay from 'components/Layout/Overlay';
import TooltipWrapper from 'components/Tooltip';
import {useTooltip} from 'components/Tooltip/hook';
import useFormError from 'hooks/useFormErrors';
import useOutsideDetector from 'hooks/useOutsideDetector';
import {Option} from 'types';
import {cleanEmptyString} from 'utils/string';
import Field from '../Field';
import {FormContextString, InputProps} from '../types';
import {getRegisterRules} from '../utils';

const Divider = () => <div className="border-r-grey-400 border-r" />;

type InputTextDropdownProps = InputProps & {
    buttonTooltipMessage?: ReactNode;
    noOptionsButtonTooltipMessage?: ReactNode;
    options?: Array<Option>;
};

const InputTextDropdown: FC<InputTextDropdownProps> = ({
    buttonTooltipMessage = '',
    className,
    classNameInput,
    disabled,
    error,
    helpText,
    id,
    label,
    maxLength,
    name,
    noOptionsButtonTooltipMessage,
    options = [],
    readOnly,
    required,
    requiredType,
    rules,
    ...rest
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const refPortal = useRef<HTMLDivElement>(null);

    const {setTooltipRef} = useTooltip();

    const {register, setValue, watch} = useFormContext<FormContextString>();

    const registerRules = getRegisterRules({disabled, required, rules});

    const {ref, ...registration} = register(name, registerRules);

    const [show, setShow] = useState(false);

    const {t} = useTranslation();

    const handleShow = useCallback(() => setShow(true), []);
    const handleHide = useCallback(() => setShow(false), []);
    const handleSelect = useCallback(
        (value: string) => {
            setValue(name, value);
            handleHide();
        },
        [handleHide, name, setValue],
    );
    useOutsideDetector([containerRef, refPortal], () => handleHide());

    const nonEmptyOptions = useMemo(
        () =>
            options.filter(
                (option) =>
                    cleanEmptyString(String(option.label)) && option.label,
            ),
        [options],
    );

    const value = watch(name);

    const hasOptions = options.length > 0;

    const tooltipMessage = hasOptions
        ? buttonTooltipMessage
        : noOptionsButtonTooltipMessage || t('form:noOptions');

    const fieldError = useFormError(name, error);

    return (
        <Field
            className={clsx(
                className,
                (registerRules?.disabled || readOnly) && 'cursor-not-allowed',
            )}
            disabled={registerRules?.disabled || readOnly}
            error={fieldError}
            helpText={helpText}
            label={label}
            maxLength={maxLength}
            name={name}
            required={!!registerRules?.required}
            requiredType={requiredType}
            type="input"
        >
            <div
                ref={containerRef}
                aria-label={name}
                className={clsx(
                    'flex input-wrapper',
                    (readOnly || disabled) && 'pointer-events-none',
                    fieldError && 'input-invalid',
                )}
                tabIndex={-1}
            >
                <input
                    ref={(r) => {
                        ref(r);
                        setTooltipRef(r);
                    }}
                    aria-label={
                        rest['aria-label'] || label === null
                            ? undefined
                            : typeof label === 'string'
                              ? label
                              : name
                    }
                    className={clsx('mr-0 pr-0', classNameInput)}
                    id={id || name}
                    maxLength={maxLength}
                    readOnly={readOnly}
                    {...registration}
                    tabIndex={readOnly ? -1 : undefined}
                    type="text"
                    {...rest}
                />
                <Divider />
                <TooltipWrapper message={tooltipMessage}>
                    <Button
                        aria-label={`${name}-button`}
                        className="rounded-none"
                        data-testid={`${name}-button`}
                        disabled={disabled || !hasOptions}
                        icon={show ? 'small_arrow_up' : 'small_arrow_down'}
                        kind="secondary"
                        onClick={show ? handleHide : handleShow}
                        tabIndex={-1}
                    />
                </TooltipWrapper>
            </div>
            {hasOptions && show && (
                <Overlay
                    aria-label="dropdown-overlay"
                    className="bg-grey-10 border border-blue-400 p-1 elevation-200 max-h-[15.5rem] overflow-y-auto"
                    element={containerRef.current}
                    layoutLayer="z-dropdown"
                    layoutMethod="bottom"
                    layoutOffset={8}
                    matchWidth={true}
                >
                    <div ref={refPortal}>
                        {nonEmptyOptions.map((option) => (
                            <DropdownSelectItem
                                key={option.value}
                                isCheckable={false}
                                label={option.label}
                                onClick={() => handleSelect(option.value)}
                                selected={value === option.value}
                                value={option.value}
                            />
                        ))}
                    </div>
                </Overlay>
            )}
        </Field>
    );
};

export default InputTextDropdown;
